import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { removeSnackbar } from 'actions';
import { Alert } from 'components';

let displayed = [];

/**
 * notistack content wrapper that exposes a STABLE test hook for e2e:
 * `data-testid="app-toast"` + `data-variant`, independent of i18n text.
 * forwardRef so notistack's transition can attach to a real DOM node.
 */
const AppToast = React.forwardRef(function AppToast(
  { variant, message },
  ref,
) {
  // data-variant must be on a DESCENDANT of [data-testid="app-toast"]:
  // the spec does getByTestId('app-toast').filter({ has: [data-variant=…] }).
  return (
    <div ref={ref} data-testid="app-toast">
      <div data-variant={variant || 'default'}>
        <Alert
          variant={variant || 'default'}
          message={typeof message === 'string' ? message : String(message)}
        />
      </div>
    </div>
  );
});

const Notifier = () => {
  const dispatch = useDispatch();
  const notifications = useSelector(store => store.snackBar.notifications || []);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [t] = useTranslation();

  // CENTRALIZED notification localization: actions dispatch stable English
  // sentences; the locale files map those sentences to the UI language.
  // An unmapped (e.g. backend-provided) message falls through verbatim —
  // i18next returns the lookup key itself when no translation exists.
  const localize = useCallback(
    message =>
      typeof message === 'string'
        ? t(message, { defaultValue: message, nsSeparator: false, keySeparator: false })
        : message,
    [t]
  );

  const storeDisplayed = (id) => {
    displayed = [...displayed, id];
  };

  const removeDisplayed = (id) => {
    displayed = [...displayed.filter(key => id !== key)];
  };

  React.useEffect(() => {
    notifications.forEach(({ key, message, options = {}, dismissed = false }) => {
      if (dismissed) {
        // dismiss snackbar using notistack
        closeSnackbar(key);
        return;
      }

      // do nothing if snackbar is already displayed
      if (displayed.includes(key)) return;

      // display snackbar using notistack
      enqueueSnackbar(localize(message), {
        key,
        ...options,
        content:
          options.content ||
          ((snackKey, snackMsg) => (
            <AppToast variant={options.variant} message={snackMsg} />
          )),
        onClose: (event, reason, myKey) => {
          if (options.onClose) {
            options.onClose(event, reason, myKey);
          }
        },
        onExited: (event, myKey) => {
          // remove this snackbar from redux store
          dispatch(removeSnackbar(myKey));
          removeDisplayed(myKey);
        },
      });

      // keep track of snackbars that we've displayed
      storeDisplayed(key);
    });
  }, [notifications, closeSnackbar, enqueueSnackbar, dispatch, localize]);

  return null;
};

export default Notifier;
